/**
 * Currency Management Reducer
 * จัดการ state สำหรับหน้า Currency Management
 */
import { useReducer } from "react";
import type { currencyBody } from "@/api/exchangeRateService";

// State types
type FormState = {
  currencyName: string;
  description: string;
  isActive: boolean;
};

type ModalState = {
  isOpen: boolean;
  selectedItem?: currencyBody;
};

export type CurrencyManagementState = {
  form: FormState;
  modal: ModalState;
};

// Action types
export type CurrencyManagementAction =
  | { type: "OPEN_ADD_MODAL" }
  | { type: "OPEN_EDIT_MODAL"; payload: currencyBody }
  | { type: "CLOSE_MODAL" }
  | {
      type: "UPDATE_FIELD";
      field: keyof FormState;
      value: FormState[keyof FormState];
    }
  | { type: "RESET_FORM" }
  | { type: "SAVE_SUCCESS" };

// Initial state
const initialFormState: FormState = {
  currencyName: "",
  description: "",
  isActive: true,
};

export const initialCurrencyState: CurrencyManagementState = {
  form: initialFormState,
  modal: {
    isOpen: false,
    selectedItem: undefined,
  },
};

// Reducer
export function currencyManagementReducer(
  state: CurrencyManagementState,
  action: CurrencyManagementAction,
): CurrencyManagementState {
  switch (action.type) {
    case "OPEN_ADD_MODAL":
      return {
        ...state,
        modal: {
          isOpen: true,
          selectedItem: undefined,
        },
        form: initialFormState,
      };

    case "OPEN_EDIT_MODAL":
      return {
        ...state,
        modal: {
          isOpen: true,
          selectedItem: action.payload,
        },
        form: {
          currencyName: action.payload.currency_name,
          description: action.payload.description,
          isActive: action.payload.is_active || true,
        },
      };

    case "CLOSE_MODAL":
      return {
        ...state,
        modal: {
          isOpen: false,
          selectedItem: undefined,
        },
      };

    case "UPDATE_FIELD":
      return {
        ...state,
        form: {
          ...state.form,
          [action.field]: action.value,
        },
      };

    case "RESET_FORM":
      return {
        ...state,
        form: initialFormState,
      };

    case "SAVE_SUCCESS":
      return {
        ...state,
        modal: {
          isOpen: false,
          selectedItem: undefined,
        },
        form: initialFormState,
      };

    default:
      return state;
  }
}

// Custom hook
export function useCurrencyManagementState() {
  return useReducer(currencyManagementReducer, initialCurrencyState);
}
